import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { QueryRenderer, graphql } from 'react-relay';
import { commitMutation } from 'react-relay';
import environment from 'api/environment';
import { GraphError } from 'types/general';
import { useToast } from 'components/Toasts/ToastProvider';
import Button from 'components/Button/Button';
import { ExpiryModalShell } from './ExpiryDateModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList } from '@fortawesome/pro-duotone-svg-icons';
import { faPaperclip, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { InterviewNotesModal_TokenQueryResponse } from './__generated__/InterviewNotesModal_TokenQuery.graphql';

// Replicates the delegate-site interview notes form (views/BackgroundCheckForms/
// InterviewNotes.tsx) inside the manager app. It reuses the same token-based
// submitInterviewNotes / interviewNotesUploadRequest mutations — the token is
// fetched via interviewNotesFormToken instead of arriving by email.

const tokenQuery = graphql`
  query InterviewNotesModal_TokenQuery($backgroundCheckUUID: UUID!) {
    interviewNotesFormToken(backgroundCheckUUID: $backgroundCheckUUID)
  }
`;

const uploadRequestMutation = graphql`
  mutation InterviewNotesModal_UploadRequestMutation(
    $token: String!
    $input: UploadFileMeta!
  ) {
    interviewNotesUploadRequest(token: $token, input: $input) {
      url
      successToken
    }
  }
`;

const submitMutation = graphql`
  mutation InterviewNotesModal_SubmitMutation(
    $token: String!
    $input: InterviewNotesInput!
  ) {
    submitInterviewNotes(token: $token, input: $input)
  }
`;

const useStyles = createUseStyles((theme: Theme) => ({
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 18
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: '#74777f',
    marginBottom: 7
  },
  labelHint: {
    fontWeight: 400,
    color: '#9ea2ad'
  },
  input: {
    boxSizing: 'border-box',
    minHeight: 40,
    padding: '0 14px',
    fontSize: 14,
    fontFamily: 'inherit',
    color: theme.colors.primaryBlack,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)',
      outline: 'none'
    }
  },
  textarea: {
    extend: 'input',
    minHeight: 110,
    padding: '10px 14px',
    resize: 'vertical'
  },
  uploadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },
  loading: {
    padding: '30px 0',
    textAlign: 'center',
    color: '#74777f',
    fontSize: 13
  }
}));

type FormProps = {
  formToken: string;
  onClose: () => void;
  onSubmitted?: () => void;
};

function InterviewNotesForm({ formToken, onClose, onSubmitted }: FormProps) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();

  const [yourName, setYourName] = React.useState('');
  const [employmentDecider, setEmploymentDecider] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [uploadToken, setUploadToken] = React.useState<string | undefined>(
    undefined
  );
  const [uploadedFileName, setUploadedFileName] = React.useState<
    string | undefined
  >(undefined);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const uploadChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt?.target?.files?.[0];
    if (!file) return;
    const split = file.name.split('.');
    const fType = split[split.length - 1];
    setUploading(true);

    commitMutation(environment, {
      mutation: uploadRequestMutation,
      variables: {
        token: formToken,
        input: {
          fileType: fType,
          contentLength: file.size
        }
      },
      onCompleted: async (response: any, errors) => {
        if (errors || !response.interviewNotesUploadRequest) {
          setUploading(false);
          toast({
            message: {
              variant: 'error',
              text: 'Unable to upload file, please try again'
            }
          });
          return;
        }

        // Upload to S3
        const uploadResp = await fetch(
          response.interviewNotesUploadRequest.url,
          {
            method: 'PUT',
            body: file
          }
        );

        setUploading(false);

        if (uploadResp.status !== 200) {
          toast({
            message: {
              variant: 'error',
              text: 'Unable to upload file, please try again'
            }
          });
          return;
        }

        setUploadToken(response.interviewNotesUploadRequest.successToken);
        setUploadedFileName(
          file.name.length > 25
            ? `${file.name.substring(0, 25)}....${fType}`
            : file.name
        );
      },
      onError: () => {
        setUploading(false);
        toast({
          message: {
            variant: 'error',
            text: 'Unable to upload file, please try again'
          }
        });
      }
    });
  };

  const removeUpload = () => {
    setUploadToken(undefined);
    setUploadedFileName(undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSubmit =
    !!yourName.trim() &&
    !!employmentDecider.trim() &&
    (!!notes.trim() || !!uploadToken);

  const onSubmit = () => {
    commitMutation(environment, {
      mutation: submitMutation,
      variables: {
        token: formToken,
        input: {
          yourName,
          employmentDecider,
          interviewNotes: notes,
          interviewNotesUploadToken: uploadToken
        }
      },
      onCompleted: (response: any, payloadErrors) => {
        const errors = (payloadErrors as unknown) as GraphError[] | null;
        if (errors && errors.length > 0) {
          toast({
            message: {
              variant: 'error',
              text: errors?.[0]?.extensions?.helpText ?? errors?.[0]?.message
            }
          });
          return;
        }
        if (response.submitInterviewNotes) {
          toast({
            message: { variant: 'success', text: 'Interview notes submitted' }
          });
          onClose();
          onSubmitted && onSubmitted();
        }
      },
      onError: (err: any) => {
        toast({
          message: {
            variant: 'error',
            text:
              err?.[0]?.extensions?.helpText ?? err?.[0]?.message ?? err.message
          }
        });
      }
    });
  };

  return (
    <ExpiryModalShell
      isOpen
      onClose={onClose}
      icon={faClipboardList}
      title="Add interview notes"
      subtitle="Record the interview notes and the decision to employ this candidate"
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      submitLabel="Submit interview notes"
    >
      <div className={classes.field}>
        <span className={classes.label}>Your name</span>
        <input
          className={classes.input}
          value={yourName}
          onChange={(e) => setYourName(e.target.value)}
        />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>
          Name of employment decider{' '}
          <span className={classes.labelHint}>
            - the person deciding to employ this candidate
          </span>
        </span>
        <input
          className={classes.input}
          value={employmentDecider}
          onChange={(e) => setEmploymentDecider(e.target.value)}
        />
      </div>
      <div className={classes.field}>
        <span className={classes.label}>
          Interview notes{' '}
          <span className={classes.labelHint}>
            - write them here or upload a PDF
          </span>
        </span>
        <textarea
          className={classes.textarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <input
        ref={fileInputRef}
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={uploadChange}
        multiple={false}
        type="file"
      />
      <div className={classes.uploadRow}>
        <Button
          archetype="flat"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <FontAwesomeIcon icon={faPaperclip} />{' '}
          {uploading
            ? 'Uploading…'
            : uploadedFileName ?? 'Upload interview notes (PDF)'}
        </Button>
        {uploadedFileName && (
          <Button archetype="flat" onClick={removeUpload}>
            <FontAwesomeIcon icon={faXmark} /> Remove upload
          </Button>
        )}
      </div>
    </ExpiryModalShell>
  );
}

type Props = {
  backgroundCheckUUID: string;
  onClose: () => void;
  onSubmitted?: () => void;
};

export default function InterviewNotesModal({
  backgroundCheckUUID,
  onClose,
  onSubmitted
}: Props) {
  return (
    <QueryRenderer
      environment={environment}
      query={tokenQuery}
      variables={{ backgroundCheckUUID }}
      render={({ props }) => {
        const data = props as InterviewNotesModal_TokenQueryResponse | null;
        if (!data?.interviewNotesFormToken) return null;

        return (
          <InterviewNotesForm
            formToken={data.interviewNotesFormToken}
            onClose={onClose}
            onSubmitted={onSubmitted}
          />
        );
      }}
    />
  );
}
